/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, {ReactNode, useState} from "react";
import {
    ChatContainer,
    ChatFooter,
    ChatInput,
    ChatScroll,
    ChatThemeProvider,
    getMessageSequencePosition,
} from "@unibuddy/chat-ui";
import {Box} from "@unibuddy/patron";
import {useBooleanFeatureFlag} from "ub-feature-flagging-react";

import {ChatGroupField, UniversityField} from "src/types/generated";
import {MentorAvatar} from "shared/Mentor/components/MentorAvatar/MentorAvatar";
import shouldShowPlaceholderAvatar from "shared/Chat/functions/shouldShowPlaceholderAvatar";
import {DEANONYMISE_PROSPECT_ENABLED} from "shared/constants";

import useUniversityColor from "../../../UniversityProvider/hooks/useUniversityColor";
import {Message} from "../Message/Message";
import ChatHeader from "../ChatHeader/ChatHeader";
import {SecureMessage} from "../SecureMessage/SecureMessage";
import {widgetChatTheme} from "../../theme/theme";
import {ChatInterventionBanner} from "../ChatInterventionBanner/ChatInterventionBanner";
import {EmptyAvatar} from "../ChatAvatar/EmptyAvatar";
import {MessageRequiredId, UserRequiredId} from "../../types";
import {SensitiveDataWarningPopUp} from "../ChatWarningMessagePopUp/SensitiveDataWarningPopUp";

type SendMessageProps = {
    text: string;
    chatGroupId: string;
};

type ChatProps = {
    messages: MessageRequiredId[];
    user: UserRequiredId;
    chatGroup: ChatGroupField;
    chatPopup?: ReactNode;
    queue?: ReactNode;
    university?: UniversityField;
    chatFooterHidden?: boolean;
    chatInputDisabled?: boolean;
    onSend?: (payload: SendMessageProps) => Promise<void>;
};

export function Chat({
    messages,
    user,
    chatGroup,
    chatPopup,
    university,
    chatFooterHidden,
    chatInputDisabled,
    queue,
    onSend,
}: ChatProps) {
    const [chatInput, setChatInput] = useState("");
    const [isSensitiveWarningPopUpVisible, setSensitiveWarningPopUpVisible] = useState(false);
    const color = useUniversityColor();
    const isDeanonymiseProspectEnabled = useBooleanFeatureFlag(
        DEANONYMISE_PROSPECT_ENABLED.key,
        DEANONYMISE_PROSPECT_ENABLED.value,
    );

    const chatInterventionMessages = messages.filter(
        (m) => m.sender && m.sender.accountRole === "university",
    );

    return (
        //@ts-ignore
        <ChatThemeProvider theme={widgetChatTheme(color)}>
            <ChatContainer
                borderWidth={1}
                borderColor="#e4e8eb"
                borderRadius="xsmall"
                overflow="hidden"
            >
                <ChatHeader
                    chatRulesOverride={messages.length > 1}
                    user={user}
                    chatGroup={chatGroup}
                    university={university}
                />
                {chatInterventionMessages.length > 0 ? <ChatInterventionBanner /> : null}
                <ChatScroll
                    messages={messages}
                    blur={false}
                    loading={false}
                    hasMoreMessages={false}
                >
                    {isDeanonymiseProspectEnabled ? (
                        <Box paddingTop={["xxsmall", "xsmall", "small"]} role="listitem" />
                    ) : (
                        <SecureMessage />
                    )}
                    {messages.map(
                        (message: MessageRequiredId, index: number, arr: MessageRequiredId[]) => {
                            const position = getMessageSequencePosition({
                                //@ts-ignore
                                currentMessage: messages[index],
                                //@ts-ignore
                                nextMessage: messages[index + 1],
                                //@ts-ignore
                                previousMessage: messages[index - 1],
                            });
                            const isMyMessage = message.sender.id === user?.id;
                            return (
                                <Message
                                    key={message.id}
                                    position={position}
                                    align={isMyMessage ? "right" : "left"}
                                    datestamp={message.created}
                                    university={university}
                                    hasRead={message.read}
                                    isLastMessage={index === messages.length - 1}
                                    isMyMessage={isMyMessage}
                                    avatar={
                                        // eslint-disable-next-line no-nested-ternary
                                        isMyMessage ? null : (
                                            <Box
                                                paddingRight={["xxsmall", "xsmall"]}
                                                flexShrink={0}
                                            >
                                                {shouldShowPlaceholderAvatar(index, arr) ? (
                                                    <EmptyAvatar />
                                                ) : (
                                                    <MentorAvatar mentor={message.sender} />
                                                )}
                                            </Box>
                                        )
                                    }
                                >
                                    {message.text}
                                </Message>
                            );
                        },
                    )}
                </ChatScroll>
                {queue}
                {chatPopup || null}
                {isSensitiveWarningPopUpVisible && (
                    <SensitiveDataWarningPopUp
                        onCloseClick={() => setSensitiveWarningPopUpVisible(false)}
                    />
                )}
                {!chatFooterHidden && (
                    <ChatFooter>
                        <ChatInput
                            onSend={(text) => {
                                (async () => {
                                    await onSend?.({text, chatGroupId: chatGroup.id});
                                })();
                            }}
                            value={chatInput}
                            onChangeText={setChatInput}
                            disabled={chatInputDisabled}
                        />
                    </ChatFooter>
                )}
            </ChatContainer>
        </ChatThemeProvider>
    );
}
